import {FC} from "react";
import Image from "next/image";
import styles from './UrlResolverComponent.module.scss';
import facebook from '@/app/assets/icons/socials/facebook.svg';
import instagram from '@/app/assets/icons/socials/instagram.svg';
import tiktok from '@/app/assets/icons/socials/tiktok.svg';
import twitch from '@/app/assets/icons/socials/twitch.svg';
import twitter from '@/app/assets/icons/socials/twitter.svg';
import x from '@/app/assets/icons/socials/x.svg';
import youtube from '@/app/assets/icons/socials/youtube.svg';
import telegram from '@/app/assets/icons/socials/telegram.svg';

interface UrlResolverComponentProps {
  className?: string;
  text: string;
}

interface UrlResolverProps {
  url: string;
}

enum ResolverType {
  Instagram = 'Instagram',
  Youtube = 'Youtube',
  Facebook = 'Facebook',
  Twitter = 'Twitter',
  X = 'X',
  TikTok = 'TikTok',
  Twitch = 'Twitch',
  Telegram = 'Telegram',
}

const socialIcons = {
  [ResolverType.Facebook]: facebook,
  [ResolverType.Instagram]: instagram,
  [ResolverType.TikTok]: tiktok,
  [ResolverType.Twitch]: twitch,
  [ResolverType.Twitter]: twitter,
  [ResolverType.X]: x,
  [ResolverType.Youtube]: youtube,
  [ResolverType.Telegram]: telegram,
};

const UrlResolver: FC<UrlResolverProps> = ({ url }) => {
  const domain_matches = url.match(/https?:\/\/(?:www\.)?(?:m\.)?(instagram\.com|youtu\.be|youtube\.com|facebook\.com|fb\.com|twitter\.com|x\.com|tiktok\.com|t\.co|twitch\.tv|telegram\.me|t\.me)/);
  const domain = domain_matches ? domain_matches[1] : '';

  if (!domain) {
    return <>{url}</>;
  }

  let resolverType: ResolverType | undefined;
  switch (domain) {
    case 'instagram.com':
      resolverType = ResolverType.Instagram;
      break;
    case 'youtube.com':
    case 'youtu.be':
      resolverType = ResolverType.Youtube;
      break;
    case 'facebook.com':
    case 'fb.com':
      resolverType = ResolverType.Facebook;
      break;
    case 'twitter.com':
    case 'x.com':
    case 't.co':
      resolverType = ResolverType.Twitter;
      break;
    case 'tiktok.com':
      resolverType = ResolverType.TikTok;
      break;
    case 'twitch.tv':
      resolverType = ResolverType.Twitch;
      break;
    case 'telegram.me':
    case 't.me':
      resolverType = ResolverType.Telegram;
      break;
    default:
      return <>{url}</>;
  }

  if (!resolverType) {
    return <>{url}</>;
  }

  const iconSrc = socialIcons[resolverType];

  return (
    <a className={styles.link} href={url} target="_blank" rel="noopener noreferrer">
      <Image src={iconSrc} alt={resolverType} />
      {resolverType}
    </a>
  );
};

const UrlResolverComponent: FC<UrlResolverComponentProps> = ({ className = '', text }) => {
  const urls = text.match(/(https?:\/\/\S+[\w?=/])/g);

  if (!urls) {
    return <div className={`${styles.root} ${className}`}><span>{text}</span></div>;
  }

  const parts = text.split(/(https?:\/\/\S+[\w?=/])/g);

  return (
    <div className={`${styles.root} ${className}`}>
      {parts.map((part, index) => {
        if (urls.includes(part)) {
          return <UrlResolver key={index} url={part} />;
        }
        return <span key={index}>{part}</span>;
      })}
    </div>
  );
};

export default UrlResolverComponent;
